import { styled, keyframes } from 'styled-components';
import { sliderArrowStyles } from '@/styles/sliderArrows';

export const Container = styled.div`
  position: absolute;
  bottom: 20px;
  width: 100%;
  margin: 0 auto;
  z-index: 100;

  @media (max-width: 400px) {
    bottom: 5px;
  }
`;

export const Box = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  /* min-width:0 so this flex row (and its SliderWrapper child) can shrink rather
     than be forced wide by the slick track's intrinsic width. */
  min-width: 0;
`;

export const SliderWrapper = styled.div`
  width: 100% !important;
  /* This is a flex item (parent Box is display:flex). Without min-width:0 the
     flexbox min-width:auto rule lets react-slick's ~9292px .slick-track force
     this item wide despite overflow:hidden, which pushed past the viewport and
     opened the white gutter beside the map. min-width:0 lets it shrink so the
     overflow:hidden below actually clips the track. */
  min-width: 0;
  position: relative;
  overflow: hidden;
  /* top band holds the dots so they aren't clipped by overflow: hidden */
  padding-top: 26px;

  .slick-list {
    overflow: hidden;
  }

  .slick-slide {
    padding: 0 40px;
    transition: all 0.3s ease;
  }

  .slick-track {
    display: flex;
  }

  /* "more cards" indicator over the map. Dots must read on dark satellite
     imagery, so light dots sit on a faint dark pill (top band, not clipped). */
  .slick-dots {
    position: absolute;
    top: 3px;
    /* left:0/right:0 + margin:auto symmetrically centres the pill on the carousel
       (the requested fix). Opacity/border kept as before - no visual change beyond
       centring. */
    left: 0;
    right: 0;
    width: fit-content;
    margin: 0 auto;
    padding: 5px 10px;
    /* !important beats react-slick's inline style="display:block" on the <ul>,
       which would otherwise stack the dots vertically. */
    display: flex !important;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 7px;
    list-style: none;
    background: rgba(0, 0, 0, 0.45);
    border-radius: 999px;
    /* collapse the inherited text line-height so the dot's line box is the dot's
       height, not ~16px - otherwise the dot sits on the baseline (bottom) and the
       pill looks bottom-heavy. */
    line-height: 0;
  }
  .slick-dots li {
    /* flex-centre the dot inside the li so it's vertically centred in the pill */
    display: flex;
    align-items: center;
    width: auto;
    height: auto;
    margin: 0;
  }
  .slick-dots li button {
    width: 7px;
    height: 7px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.55);
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.5);
    font-size: 0;
    cursor: pointer;
    transition: background 0.2s ease;
  }
  .slick-dots li button::before {
    content: none;
  }
  .slick-dots li.slick-active button {
    background: #ffffff;
  }

  /* shared arrow styling: white button + centred geometric chevron, visible on map */
  ${sliderArrowStyles}

  @media (max-width: 400px) {
    .slick-slide {
      padding: 0 10px;
    }
  }
`;

export const Card = styled.div<{ type: string }>`
  display: flex !important;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;

  cursor: pointer;
  width: 24%;
  height: 150px;
  background-color: #fff;
  border-radius: 10px;
  padding: 10px;
  transition:
    background-color 0.3s ease,
    filter 0.3s ease;

  &:hover {
    background-color: ${(props) => {
      switch (props.type) {
        case 'factories':
          return '#6A88B2';
        case 'farmers':
          return '#DB9030';
        case 'forests':
          return '#84A341';
        default:
          return '#fff';
      }
    }};
    color: #fff;

    div > div > img {
      filter: brightness(0) invert(1);
    }
  }

  @media (max-width: 400px) {
    gap: 10px;
    height: 100px;
  }
`;

export const Image = styled.img`
  width: 200px;
  height: 100%;
  border-radius: 10px;
  object-fit: cover;
  object-position: center;

  @media (max-width: 550px) {
    width: 100px;
    height: 100px;
  }

  @media (max-width: 400px) {
    width: 70px;
    height: 70px;
  }

  @media (max-width: 300px) {
    width: 70px;
  }
`;

export const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 80%;
  gap: 10px;
`;
export const Title = styled.p`
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;

  @media (max-width: 550px) {
    font-size: 16px;
  }

  @media (max-width: 400px) {
    font-size: 14px;
  }

  @media (max-width: 300px) {
    font-size: 12px;
  }
`;

export const Description = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  align-items: center;

  p {
    font-family: 'Roboto', sans-serif;
    line-height: 15px;
    font-size: 15px;
    font-weight: 400;
    margin: 0;
  }

  @media (max-width: 550px) {
    img {
      width: 15px;
    }
    p {
      font-size: 14px;
    }
  }

  @media (max-width: 400px) {
    img {
      width: 15px;
    }
    p {
      font-size: 12px;
    }
  }

  @media (max-width: 300px) {
    p {
      font-size: 10px;
    }
  }
`;

// Second, smaller line under a card figure ("registered in FairChain
// programmes"). Inherits colour so it turns white with the card on hover.
export const SubText = styled.span`
  display: block;
  font-size: 11px;
  line-height: 13px;
  margin-top: 3px;
  opacity: 0.85;

  @media (max-width: 400px) {
    font-size: 10px;
    line-height: 12px;
  }
`;

const dotsAnimation = keyframes`
  20% {
    background-position: 0% 0%, 50% 50%, 100% 50%;
  }
  40% {
    background-position: 0% 100%, 50% 0%, 100% 50%;
  }
  60% {
    background-position: 0% 50%, 50% 100%, 100% 0%;
  }
  80% {
    background-position: 0% 50%, 50% 50%, 100% 100%;
  }
`;

export const Dots = styled.div`
  position: absolute;
  width: 44px;
  height: 21.1px;
  background:
    radial-gradient(circle closest-side, #e5007d 90%, #0000) 0% 50%,
    radial-gradient(circle closest-side, #e5007d 90%, #0000) 50% 50%,
    radial-gradient(circle closest-side, #e5007d 90%, #0000) 100% 50%;
  background-size: calc(100% / 3) 10px;
  background-repeat: no-repeat;
  animation: ${dotsAnimation} 0.8s infinite linear;
`;

export const Wrapper = styled.div`
  position: relative;
  top: 0;
  left: 18%;
`;
